import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleNotch, Info } from '@phosphor-icons/react';

import { platformApi, getPlatformApiErrorMessage } from '@/lib/platformApi';
import { usePlatformAuthStore } from '@/features/platform-auth/store';
import {
  PlatformFieldRow,
  PlatformPanel,
  PlatformSaveIndicator,
  PlatformSectionIntro,
  PlatformTextInput,
  usePlatformSaveState,
} from '@/components/platform/PlatformSettingsPrimitives';

/**
 * My Account — the Platform Console operator's own details.
 *
 * WHAT IS EDITABLE HERE IS A FACT ABOUT THE MODEL, not a design choice. `PlatformAdmin`
 * (apps/api/prisma/schema.prisma) has exactly four columns that describe a person: `email`,
 * `firstName`, `lastName` and `status`. Everything else on that model is authentication
 * (`passwordHash`, `twoFaSecret`) or system-written (`lastLoginAt`, `lastLoginIp`,
 * `passwordChangedAt`, `createdAt`).
 *
 * So the name is editable, and NOTHING ELSE IS. There is no phone, job title, display name,
 * avatar, signature, biography, year of call, Inn or chambers membership on this account —
 * those belong to the chambers-side `User` / `Barrister` models and would be invented fields
 * here. The chambers My Account page is the UX reference for this screen; it is not the field
 * list.
 *
 * SELF ONLY. Every request on this page goes to `/auth/me`, which resolves the account from the
 * platform JWT and takes no id. Administering other operators is Users and Support Executives.
 */

interface AdminAccount {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  status: string;
  createdAt: string;
}

const ME_KEY = ['platform', 'auth', 'me'] as const;

function formatDate(str?: string) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Mirror of `nameSchema` in apps/api/src/lib/field-validators.ts.
 *
 * A COURTESY check so the operator gets the rule stated beside the field instead of an opaque
 * rejection — the server is the actual control, and it re-validates everything sent to it. The
 * API cannot import from the web package, so this is a deliberate mirror. Keep the two in step.
 */
const NAME_ALLOWED = /^[A-Za-zÀ-ÖØ-öø-ÿĀ-ɏ'’.\-\s]+$/;
const NAME_HAS_LETTER = /[A-Za-zÀ-ÖØ-öø-ÿĀ-ɏ]/;
const NAME_MAX = 80;

function nameError(value: string): string | null {
  const v = value.trim();
  if (v.length === 0) return 'Required';
  if (v.length > NAME_MAX) return `Must be ${NAME_MAX} characters or fewer`;
  if (!NAME_ALLOWED.test(v)) return 'Only letters, spaces, hyphens, apostrophes and periods are allowed';
  if (!NAME_HAS_LETTER.test(v)) return 'Must contain at least one letter';
  return null;
}

/**
 * A name field that saves when the operator leaves it, not on every keystroke.
 *
 * Same interaction as the chambers My Account page, for the same reason: there is no form to
 * submit and nothing to lose track of, and the outcome is reported beside the field that
 * changed. A rejected save rolls the box back to the stored value rather than leaving the
 * screen showing a name the server did not keep.
 */
function SavedName({
  value,
  placeholder,
  ariaLabel,
  onSave,
}: {
  value: string;
  placeholder: string;
  ariaLabel: string;
  onSave: (v: string) => Promise<unknown>;
}) {
  const [draft, setDraft] = useState(value);
  const [invalid, setInvalid] = useState<string | null>(null);
  const { state, error, run } = usePlatformSaveState();
  const committed = useRef(value);

  // Adopt the server's value when it changes underneath us — but never mid-edit.
  useEffect(() => {
    if (committed.current !== value) {
      committed.current = value;
      setDraft(value);
    }
  }, [value]);

  async function commit() {
    const next = draft.trim();
    if (next === committed.current) {
      setInvalid(null);
      return;
    }
    const problem = nameError(next);
    if (problem) {
      setInvalid(problem);
      return;
    }
    setInvalid(null);
    const ok = await run(() => onSave(next));
    if (ok) committed.current = next;
    else setDraft(committed.current);
  }

  return (
    <div>
      <PlatformTextInput
        value={draft}
        onChange={(v) => { setDraft(v); if (invalid) setInvalid(nameError(v.trim())); }}
        onBlur={() => void commit()}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        placeholder={placeholder}
        aria-label={ariaLabel}
        invalid={!!invalid}
      />
      <div className="mt-1 min-h-[1rem]">
        {invalid ? (
          <span className="text-xs text-plat-danger-text">{invalid}</span>
        ) : (
          <PlatformSaveIndicator state={state} error={error} />
        )}
      </div>
    </div>
  );
}

export default function PlatformSettingsAccountPage() {
  const qc = useQueryClient();
  const patchAdmin = usePlatformAuthStore((s) => s.patchAdmin);

  const { data, isLoading, error, refetch } = useQuery<{ admin: AdminAccount }>({
    queryKey: ME_KEY,
    queryFn: async () => {
      const res = await platformApi.get('/auth/me');
      return res.data?.data ?? res.data;
    },
  });

  const save = useMutation({
    mutationFn: async (input: { firstName?: string; lastName?: string }) => {
      const res = await platformApi.patch('/auth/me', input);
      return (res.data?.data ?? res.data) as { admin: AdminAccount };
    },
    onSuccess: (updated) => {
      qc.setQueryData(ME_KEY, updated);
      // The sidebar footer and the topbar render the name from the session store, not from this
      // query — without this the operator renames themselves and the console keeps calling them
      // the old name until they sign in again.
      patchAdmin({ firstName: updated.admin.firstName, lastName: updated.admin.lastName });
    },
  });

  if (isLoading) {
    return (
      <div className="flex h-40 items-center justify-center" role="status" aria-label="Loading your account">
        <CircleNotch className="h-5 w-5 animate-spin text-plat-muted" />
      </div>
    );
  }

  const admin = data?.admin;

  if (error || !admin) {
    return (
      <div className="rounded-xl border border-plat-danger-line bg-plat-danger-soft p-6 text-center">
        <p className="text-sm font-medium text-plat-danger-text">
          {error ? getPlatformApiErrorMessage(error) : 'Your account details could not be loaded'}
        </p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="mt-3 rounded-lg border border-plat-border bg-plat-surface px-3 py-1.5 text-xs font-medium text-plat-fg hover:bg-plat-hover"
        >
          Try again
        </button>
      </div>
    );
  }

  const patch = (input: { firstName?: string; lastName?: string }) => save.mutateAsync(input);

  return (
    <div className="space-y-5">
      <PlatformSectionIntro
        title="My Account"
        description="Your name, your sign-in address, and how this operator account appears across the console"
      />

      <PlatformPanel
        title="Who you are"
        description="Shown in the console and recorded against every action you take in the platform audit log"
      >
        <PlatformFieldRow label="Name">
          <div className="flex flex-col gap-2 sm:flex-row">
            <SavedName
              value={admin.firstName}
              placeholder="First name"
              ariaLabel="First name"
              onSave={(v) => patch({ firstName: v })}
            />
            <SavedName
              value={admin.lastName}
              placeholder="Last name"
              ariaLabel="Last name"
              onSave={(v) => patch({ lastName: v })}
            />
          </div>
        </PlatformFieldRow>

        {/* Read-only by design. Changing the address an account signs in with is an identity
            change, not a profile edit: done properly it means verifying the new address and
            holding the old one until it is confirmed. The Platform Console has no such flow —
            and unlike the chambers side there is no support desk above this account to ask, so
            the field says what is actually true rather than offering a control that would
            either fail or, worse, quietly lock the operator out of the console. */}
        <PlatformFieldRow
          label="Email address"
          readOnly
          readOnlyValue={admin.email}
          readOnlyNote={
            <>
              This is the address you sign in with. The Platform Console has no verified
              email-change flow, so it cannot be changed here or anywhere else in the console —
              a replacement operator account has to be invited under the new address from
              Support Executives.
            </>
          }
        />

        <PlatformFieldRow
          label="Account status"
          readOnly
          readOnlyValue={<span className="capitalize">{admin.status}</span>}
          readOnlyNote="Set by another operator on Support Executives. An account cannot change its own status."
        />
      </PlatformPanel>

      {/*
        No roles, no permissions, no chambers.
        `PlatformAdmin` has no role column: every active operator holds the same full access,
        which is the trust model recorded in platform-team.routes.ts. Rendering an empty "Roles"
        list here — the shape the chambers page uses — would imply tiers that do not exist, so
        the screen states the position instead.
      */}
      <PlatformPanel
        title="Access"
        description="What this account can reach in the Platform Console"
      >
        <div className="flex items-start gap-2 text-sm text-plat-fg">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-plat-info-text" />
          <div>
            <p>The Platform Console has no role tiers.</p>
            <p className="mt-1 text-xs text-plat-muted">
              Every active operator account holds the same full access: tenants, users, billing,
              support and the platform audit log. Access is not granted or withheld per
              operator, so there is nothing to display per account and nothing here to change.
              Whether an account may sign in at all is its status, above.
            </p>
          </div>
        </div>
      </PlatformPanel>

      {/* The sign-in record — last sign-in, its address, when the password last changed — is on
          the SECURITY section, not here. It is the evidence an operator checks when something
          looks wrong, so it belongs beside the control that answers it. Repeating it on both
          pages would leave two places to look and two places to get out of step. */}
      <PlatformPanel
        title="Account record"
        description="Written by the platform when this operator account was created"
      >
        <PlatformFieldRow
          label="Operator since"
          readOnly
          readOnlyValue={formatDate(admin.createdAt)}
          readOnlyNote="Recorded when the account was created. Sign-in history is on the Security section."
        />
      </PlatformPanel>

      {save.isError && (
        <p className="text-xs text-plat-danger-text">{getPlatformApiErrorMessage(save.error)}</p>
      )}
    </div>
  );
}
